import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/data/products";
import { Menu, X, Phone } from "lucide-react";

const NAV_LINKS = [
  { href: "/#home", label: "Home" },
  { href: "/#collections", label: "Collections" },
  { href: "/#heritage", label: "Heritage" },
  { href: "/blog", label: "Journal" },
  { href: "/#about", label: "About" },
  { href: "/#contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const resolveHref = (href) => {
    if (href.startsWith("/#")) {
      return isHome ? href.slice(1) : href; // "#home" on home page, "/#home" from other pages
    }
    return href;
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-cream-100/85 backdrop-blur-md border-b border-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-24 md:h-28">
        <a
          href={isHome ? "#home" : "/"}
          data-testid="brand-logo-link"
          className="flex items-center gap-4 group"
        >
          <div className="relative">
            <div className="absolute inset-0 rounded-full border border-gold/60 group-hover:border-gold transition-colors duration-500 scale-110" />
            <div className="absolute inset-0 rounded-full border border-gold/25 scale-[1.28]" />
            <img
              src={BRAND.logo}
              alt="Vardaan Arts Logo"
              className="relative w-16 h-16 md:w-[72px] md:h-[72px] object-contain drop-shadow-sm"
            />
          </div>
          <div className="hidden sm:block leading-none">
            <div className="font-serif text-[26px] md:text-[28px] tracking-[0.14em] text-ink font-semibold">
              VARDAAN ARTS
            </div>
            <div className="text-[10px] tracking-[0.32em] text-gold-dark uppercase mt-2 font-medium">
              Crafting Divine Beauty
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={resolveHref(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-[13px] tracking-[0.2em] uppercase text-ink hover:text-gold-dark transition-colors duration-300 relative py-2"
            >
              <span>{l.label}</span>
              <span className="absolute left-0 -bottom-1 h-[1px] w-0 bg-gold transition-[width] duration-500 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${BRAND.phone}`}
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-[13px] tracking-wide text-ink hover:text-gold-dark transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden xl:inline">{BRAND.phoneDisplay}</span>
          </a>
          <a
            href={isHome ? "#contact" : "/#contact"}
            data-testid="header-inquire-btn"
            className="px-6 py-3 bg-gold text-cream-100 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-gold-dark transition-colors duration-300 border border-gold hover:border-gold-dark whitespace-nowrap text-center"
          >
            Request a Quote
          </a>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-ink"
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-cream-100 border-t border-gold/20"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={resolveHref(l.href)}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.2em] uppercase text-ink hover:text-gold-dark"
              >
                {l.label}
              </a>
            ))}
            <a
              href={isHome ? "#contact" : "/#contact"}
              onClick={() => setOpen(false)}
              className="px-6 py-3 bg-gold text-cream-100 text-center text-[12px] tracking-[0.22em] uppercase mt-2"
            >
              Request a Quote
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
